import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

export interface MetaData {
    title?: string;
    description?: string;
    keywords?: string;
    siteTitle?: string;
    imageUrl?: string;
    pageUrl?: string;
    ext?: string;
}

@Injectable({
    providedIn: 'root'
})
export class MetaService {
    private readonly defaults = {
        title: 'XipeTech - Innovative IT Solutions',
        description: 'Welcome to XipeTech, we provide the most responsive and functional IT Services and solve business challenges with innovative technologies.',
        keywords: 'IT services, software development, mobile app development, web development, android development, iOS development',
        siteTitle: 'XipeTech',
        imageUrl: `${window.location.origin}/assets/img/xipetech.png`,
        ext: 'png'
    };

    constructor(private meta: Meta, private titleService: Title, private router: Router) { }

    setMetaData(data: MetaData): void {
        try {
            const baseUrl = window.location.origin;
            const pageUrl = `${baseUrl}${this.router.url}`;

            // Merge provided data with defaults
            const config = { ...this.defaults, ...data, pageUrl };

            // Set page title
            this.titleService.setTitle(config.title);

            // Set basic meta tags
            this.setBasicMetaTags(config);

            // Set Open Graph tags
            this.setOpenGraphTags(config);

            // Set Twitter tags
            this.setTwitterTags(config);

        } catch (error) {
            console.error('Error setting meta data:', error);
            // Fallback to defaults if error occurs
            this.setFallbackMetaTags();
        }
    }

    private setBasicMetaTags(config: any): void {
        this.meta.updateTag({ name: 'description', content: config.description });
        this.meta.updateTag({ name: 'keywords', content: config.keywords });
        this.meta.updateTag({ name: 'author', content: 'Abdul Fattah' });
        this.meta.updateTag({ name: 'MobileOptimized', content: '320' });
        this.meta.updateTag({ itemprop: 'image', content: config.imageUrl });
    }

    private setOpenGraphTags(config: any): void {
        this.meta.updateTag({ property: 'og:title', content: config.title });
        this.meta.updateTag({ property: 'og:description', content: config.description });
        this.meta.updateTag({ property: 'og:url', content: config.pageUrl });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:site_name', content: config.siteTitle });
        this.meta.updateTag({ property: 'og:updated_time', content: new Date().toISOString() });
        this.meta.updateTag({ property: 'og:image', content: config.imageUrl });
        this.meta.updateTag({ property: 'og:image:secure_url', content: config.imageUrl });
        this.meta.updateTag({ property: 'og:image:width', content: '436' });
        this.meta.updateTag({ property: 'og:image:height', content: '228' });
        this.meta.updateTag({ property: 'og:image:alt', content: config.siteTitle });
        this.meta.updateTag({ property: 'og:image:type', content: `image/${config.ext}` });
    }

    private setTwitterTags(config: any): void {
        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:domain', content: new URL(config.pageUrl).hostname });
        this.meta.updateTag({ name: 'twitter:title', content: config.title });
        this.meta.updateTag({ name: 'twitter:description', content: config.description });
        this.meta.updateTag({ name: 'twitter:site', content: '@xipetech' });
        this.meta.updateTag({ name: 'twitter:creator', content: '@Admin' });
        this.meta.updateTag({ name: 'twitter:image', content: config.imageUrl });
    }

    private setFallbackMetaTags(): void {
        this.titleService.setTitle(this.defaults.title);
        this.meta.updateTag({ name: 'description', content: this.defaults.description });
        this.meta.updateTag({ name: 'keywords', content: this.defaults.keywords });
    }
}